import { cloneDeep } from 'lodash';
import { Framework } from '@/framework';
import type { StageOperate } from '@/plugins/apps/user-editor/stage-operate';
import { ResizeOperateType } from '@/version-types';
import { REFERENCE_LINE_DIRECTION, REFERENCE_LINES_STAGE_HISTORY_NAMESPACE } from './constants';
import { isReferenceLinesStageHistoryExtension } from './stage-history-extension';
import { getReferenceLinesState } from './states/reference-lines-state';
import type { ReferenceLine, ReferenceLinesStageHistoryExtension } from './types';

/** 舞台尺寸调整开始时记录的舞台矩形。 */
interface OriginStageRect {
    /** 当前舞台顶部在编辑器坐标系中的位置。 */
    top: number;
    /** 当前舞台底部在编辑器坐标系中的位置。 */
    bottom: number;
    /** 当前舞台高度，单位为编辑器逻辑像素。 */
    height: number;
    /** 当前舞台宽度，单位为编辑器逻辑像素。 */
    width: number;
}

/**
 * reference-lines 对 StageOperate 的上层业务适配器。
 * 负责参考线位置同步、舞台历史快照采集和精确快照恢复。
 */
export class ReferenceLinesStageOperateController extends Framework {
    /** 当前舞台尺寸操作开始时的参考线深拷贝。 */
    private localReferenceLines: ReferenceLine[] = [];

    /** 当前舞台尺寸操作开始时的舞台矩形。 */
    private originStageRect: OriginStageRect = {
        top: 0,
        bottom: 0,
        height: 0,
        width: 0,
    };

    /**
     * 订阅 StageOperate 实例事件。
     * StageOperate 未注册时返回空清理函数，不影响其他参考线能力。
     */
    mount() {
        const stageOperate = this.framework.service.registration.getInstance<StageOperate>('StageOperate');
        if (!stageOperate) return () => {};

        const cleanups = [
            stageOperate.onResizeStart(() => this.prepare()),
            stageOperate.onResizing(({ newSize, oldSize, operateType }) => {
                this.adjustReferenceLines(newSize, oldSize, operateType);
            }),
            stageOperate.onCollectHistoryExtensions((_context, collector) => {
                const extension: ReferenceLinesStageHistoryExtension = {
                    oldLines: cloneDeep(this.localReferenceLines),
                    newLines: cloneDeep(getReferenceLinesState(this.identity).referenceLines),
                };
                collector.set(REFERENCE_LINES_STAGE_HISTORY_NAMESPACE, extension);
            }),
            stageOperate.onApplyHistoryExtensions(({ extensions, isRedo, isUndo }) => {
                const extension = extensions[REFERENCE_LINES_STAGE_HISTORY_NAMESPACE];
                if (!isReferenceLinesStageHistoryExtension(extension)) return;
                const setReferenceLines = getReferenceLinesState(this.identity).setReferenceLines;
                if (isUndo) setReferenceLines(cloneDeep(extension.oldLines));
                if (isRedo) setReferenceLines(cloneDeep(extension.newLines));
            }),
            stageOperate.onResizeEnd(() => {
                this.localReferenceLines = [];
            }),
        ];

        return () => cleanups.forEach((cleanup) => cleanup());
    }

    /** 保存位置计算基准和同一条舞台历史使用的旧参考线快照。 */
    private prepare() {
        this.localReferenceLines = cloneDeep(getReferenceLinesState(this.identity).referenceLines);
        this.originStageRect = this.getStageRect();
    }

    /** 获取活动舞台在编辑器坐标系中的原始矩形。 */
    private getStageRect(): OriginStageRect {
        const stageIndex = this.framework.service.frame.getActiveIndex();
        const { width, height } = this.framework.service.frame.getActiveFrameSize();
        const top = this.framework.service.frame.getStageTop(stageIndex);
        return {
            top,
            bottom: top + height,
            height,
            width,
        };
    }

    /** 按重构前的方向与阈值规则，从 start 快照重新计算参考线位置。 */
    private adjustReferenceLines(newSize: number, oldSize: number, operateType: ResizeOperateType) {
        const state = getReferenceLinesState(this.identity);
        const deltaSize = newSize - oldSize;
        const oldBottom = this.originStageRect.bottom;
        const oldTop = this.originStageRect.top;
        const newRight = this.framework.service.frame.getSize().width;

        // 每一帧先重置为 start 快照，避免累计位移。
        state.setReferenceLines(this.localReferenceLines);

        switch (operateType) {
            case ResizeOperateType.BC:
                this.localReferenceLines.forEach((line) => {
                    if (line.direction !== REFERENCE_LINE_DIRECTION.HORIZONTAL) return;
                    if (line.position > oldBottom) {
                        state.updateReferenceLine({ id: line.id, position: line.position + deltaSize });
                    }
                });
                break;
            case ResizeOperateType.TC:
                this.localReferenceLines.forEach((line) => {
                    if (line.direction !== REFERENCE_LINE_DIRECTION.HORIZONTAL) return;
                    if (line.position > oldTop) {
                        state.updateReferenceLine({ id: line.id, position: line.position + deltaSize });
                    }
                });
                break;
            case ResizeOperateType.LC:
                this.localReferenceLines.forEach((line) => {
                    if (line.direction !== REFERENCE_LINE_DIRECTION.VERTICAL) return;
                    state.updateReferenceLine({ id: line.id, position: line.position + deltaSize });
                });
                break;
            case ResizeOperateType.RC:
                this.localReferenceLines.forEach((line) => {
                    if (line.direction !== REFERENCE_LINE_DIRECTION.VERTICAL) return;
                    if (line.position > newRight) {
                        state.updateReferenceLine({ id: line.id, position: line.position + deltaSize });
                    }
                });
                break;
        }
    }
}
